import { UzButton, UzCombobox, UzFormField, UzInput } from '@utazzo/ui';
import { Sparkles } from 'lucide-react';
import { useMemo } from 'react';
import { useFieldArray, useWatch, type UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';

import { useCollectionCandidates } from '../use-destination-pages';

import { AddRowButton, RowShell } from './RowShell';

import type { CreateWebsiteDestinationPageRequest } from '@utazzo/shared';

import { useMarketingTagsList } from '@/features/marketing-tags/use-marketing-tags';

export const SlidersEditor = ({
  form,
  disabled,
}: {
  form: UseFormReturn<CreateWebsiteDestinationPageRequest>;
  disabled?: boolean;
}) => {
  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: 'sliders',
  });
  const destinationId = useWatch({ control: form.control, name: 'destinationId' });

  const tagsQuery = useMarketingTagsList({
    page: 1,
    pageSize: 200,
    sortDir: 'asc' as const,
    type: 'collection',
    isActive: true,
  });
  const candidatesQuery = useCollectionCandidates(destinationId ?? undefined);

  const candidateCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of candidatesQuery.data ?? []) map.set(c.marketingTagId, c.packageCount);
    return map;
  }, [candidatesQuery.data]);

  const options = useMemo(
    () =>
      (tagsQuery.data?.data ?? []).map((t) => {
        const count = candidateCounts.get(t.id);
        return {
          value: t.id,
          label: t.name,
          description:
            count !== undefined ? `${count} package${count === 1 ? '' : 's'}` : 'No packages yet',
        };
      }),
    [tagsQuery.data, candidateCounts],
  );

  const tagById = useMemo(() => {
    const map = new Map<string, { name: string; description: string | null }>();
    for (const t of tagsQuery.data?.data ?? [])
      map.set(t.id, { name: t.name, description: t.description });
    for (const c of candidatesQuery.data ?? [])
      map.set(c.marketingTagId, { name: c.name, description: c.description });
    return map;
  }, [tagsQuery.data, candidatesQuery.data]);

  const autoFillSliders = (): void => {
    const candidates = candidatesQuery.data ?? [];
    if (!candidates.length) {
      toast.message('No collections with packages found for this destination.');
      return;
    }
    const existing = new Set(form.getValues('sliders').map((s) => s.marketingTagId));
    let added = 0;
    for (const c of candidates) {
      if (existing.has(c.marketingTagId)) continue;
      append({
        marketingTagId: c.marketingTagId,
        heading: c.name,
        subheading: c.description ?? '',
      });
      existing.add(c.marketingTagId);
      added += 1;
    }
    if (added === 0) toast.message('All collections with packages are already added as sliders.');
    else toast.success(`Added ${added} slider${added === 1 ? '' : 's'} from package data.`);
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-zinc-500">
        Each slider is a <strong>collection</strong> carousel - packages must be tagged with that
        collection and linked to the page&apos;s destination master.
      </p>
      {!destinationId && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Select a destination on the Details tab first. Sliders need packages scoped to that
          destination.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <UzButton
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || !destinationId || candidatesQuery.isLoading}
          loading={candidatesQuery.isFetching}
          onClick={autoFillSliders}
        >
          <Sparkles className="mr-1.5 h-3.5 w-3.5" />
          Auto-fill from packages
        </UzButton>
      </div>
      {fields.map((f, i) => (
        <RowShell
          key={f.id}
          index={i}
          total={fields.length}
          disabled={disabled}
          onMove={move}
          onRemove={remove}
        >
          <UzFormField name={`sliders.${i}.marketingTagId` as const}>
            {({ field, error }) => {
              const count =
                field.value && typeof field.value === 'string'
                  ? candidateCounts.get(field.value)
                  : undefined;
              return (
                <>
                  <UzCombobox
                    name={field.name}
                    label="Collection"
                    placeholder="Select a collection…"
                    searchPlaceholder="Search collections…"
                    value={(field.value as string | null) ?? ''}
                    onValueChange={(v) => {
                      field.onChange(v);
                      const tag = tagById.get(v);
                      if (!tag) return;
                      const heading = form.getValues(`sliders.${i}.heading`);
                      if (!heading?.trim()) form.setValue(`sliders.${i}.heading`, tag.name);
                      const sub = form.getValues(`sliders.${i}.subheading`);
                      if (!sub?.trim() && tag.description) {
                        form.setValue(`sliders.${i}.subheading`, tag.description);
                      }
                    }}
                    options={options}
                    loading={tagsQuery.isLoading}
                    error={error}
                    disabled={disabled}
                  />
                  {destinationId &&
                    field.value &&
                    count === undefined &&
                    !candidatesQuery.isLoading && (
                      <p className="mt-1 text-xs text-amber-700">
                        No active packages for this collection on the selected destination.
                      </p>
                    )}
                </>
              );
            }}
          </UzFormField>
          <UzFormField name={`sliders.${i}.heading` as const}>
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | null) ?? ''}
                label="Carousel heading"
                placeholder="Most Loved Trips"
                error={error}
                disabled={disabled}
              />
            )}
          </UzFormField>
          <UzFormField name={`sliders.${i}.subheading` as const}>
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | null) ?? ''}
                label="Carousel subheading"
                placeholder="The trips everyone's obsessing over right now."
                error={error}
                disabled={disabled}
              />
            )}
          </UzFormField>
        </RowShell>
      ))}
      <AddRowButton
        label="Add slider"
        disabled={disabled}
        onClick={() => append({ marketingTagId: '', heading: '', subheading: '' })}
      />
    </div>
  );
};
